// ============================================================================
// Reconnaissance des règles (lot 2 de la refonte, docs/moteur/REFONTE_MOTEUR.md).
//
// Une forme de règle est lue une fois (`ruleForms`) puis APPARIÉE à une ligne :
//   - sommes et produits appariés à l'ordre près (associatif-commutatif), un
//     produit de matrices dans l'ordre seulement ;
//   - une variable de motif peut prendre plusieurs termes d'une somme ou d'un
//     produit (« ?u + ?c » sur « 2x + 3 + y ») ;
//   - un coefficient du motif se divise (« − ?b² » sur « − 9 » → b² = 9) et une
//     puissance s'inverse sur un nombre ou un monôme (b² = 9 → b = 3 ; a² = 4x² → a = 2x).
// Le nombre d'appariements est borné (`maxMatches`) : jamais d'explosion.
//
// `matchRuleStep` dit si le passage d'une ligne à la suivante est l'application
// d'une règle : la forme de départ s'apparie à la ligne de départ, la forme
// d'arrivée (variables introduites lues sur la ligne écrite) est comparée :
//   - membre à membre par la forme normale pour une équation / inéquation ;
//   - à l'identique (forme canonique légère) pour une réécriture d'expression,
//     appliquée à un sous-terme (ou à une partie d'une somme / d'un produit).
// ============================================================================

import * as Q from './exact';
import { compareTerms, normalize, rfSimplify, rfToTerm, type NormalOptions } from './normal';
import type { LoadedRule } from './rules/loader';
import { parseForm, type FormRelation } from './rules/forms';
import {
  canon,
  CONSTANTS,
  div,
  fromNode,
  fn,
  involvesMatrix,
  mul,
  num,
  pow,
  sameTerm,
  substitute,
  sub,
  termKey,
  termText,
  type Rel,
  type Sorts,
  type Statement,
  type Term,
} from './term';
import type { Condition } from './types';

export type Bindings = Readonly<Record<string, Term>>;

/* ------------------------------ formes ------------------------------ */

export interface RuleForm {
  relation: FormRelation;
  sides: Term[];
}
const FORM_CACHE = new WeakMap<LoadedRule, Record<string, RuleForm>>();

/** Formes d'une règle lues en termes (cache par règle chargée). */
export function ruleForms(rule: LoadedRule): Record<string, RuleForm> {
  const hit = FORM_CACHE.get(rule);
  if (hit) return hit;
  const out: Record<string, RuleForm> = {};
  for (const [k, f] of Object.entries(rule.forms)) {
    const r = parseForm(f.text);
    if (!r.ok) throw new Error(`forme illisible ${rule.qid}/${k}`);
    out[k] = { relation: r.parsed.relation, sides: r.parsed.sides.map((s) => canon(fromNode(s))) };
  }
  FORM_CACHE.set(rule, out);
  return out;
}

/* ------------------------------ appariement ------------------------------ */

export interface MatchCtx {
  vars: Readonly<Record<string, unknown>>;
  sorts: Sorts;
  /** essais restants (borne dure) */
  fuel: { n: number };
}

/** Nom de la variable de motif, ou null (un booléen ne restreindrait pas le type). */
const varName = (t: Term, ctx: MatchCtx): string | null =>
  t.k === 'sym' && t.n in ctx.vars && !CONSTANTS[t.n] ? t.n : null;
const isVar = (t: Term, ctx: MatchCtx): boolean => varName(t, ctx) !== null;

function bind(b: Bindings, name: string, t: Term, ctx: MatchCtx): Bindings[] {
  const old = b[name];
  if (old) return sameTerm(old, t, ctx.sorts) ? [b] : [];
  return [{ ...b, [name]: canon(t, ctx.sorts) }];
}

/** Racine e-ième exacte d'un terme (nombre ou monôme), pour inverser « ?b² ». */
function rootOf(t: Term, e: number): Term[] {
  if (e === 1) return [t];
  if (t.k === 'num') {
    if (t.v.n < 0n && e % 2 === 0) return [];
    if (e === 2) {
      const r = Q.sqrtRat(t.v);
      if (!r) return [];
      return [r.rad === 1n ? num(r.coef) : mul(num(r.coef), fn('sqrt', num(Number(r.rad))))];
    }
    const nr = intRoot(t.v.n < 0n ? -t.v.n : t.v.n, e);
    const dr = intRoot(t.v.d, e);
    if (nr === null || dr === null) return [];
    return [num(Q.rat(t.v.n < 0n ? -nr : nr, dr))];
  }
  if (t.k === 'pow' && t.b.k === 'num' && Q.isInteger(t.b.v)) {
    const k = Number(t.b.v.n);
    if (k % e === 0) return [k / e === 1 ? t.a : pow(t.a, num(k / e))];
    return [];
  }
  if (t.k === 'mul') {
    const parts = t.c.map((c) => rootOf(c, e)[0]);
    return parts.every((p) => p) ? [mul(...(parts as Term[]))] : [];
  }
  return [];
}
function intRoot(n: bigint, r: number): bigint | null {
  if (n < 2n) return n;
  const x = BigInt(Math.round(Number(n) ** (1 / r)));
  for (const c of [x - 1n, x, x + 1n]) if (c >= 0n && c ** BigInt(r) === n) return c;
  return null;
}

/** Tous les appariements de `p` (motif) sur `t` (cible), à partir de `b`. */
export function matchTerm(p: Term, t: Term, b: Bindings, ctx: MatchCtx): Bindings[] {
  if (--ctx.fuel.n < 0) return [];
  const pv = varName(p, ctx);
  if (pv !== null) return bind(b, pv, t, ctx);
  switch (p.k) {
    case 'num':
      return t.k === 'num' && Q.eq(p.v, t.v) ? [b] : [];
    case 'sym':
      return t.k === 'sym' && t.n === p.n ? [b] : [];
    case 'add':
      return t.k === 'add' ? matchAC(p.c, t.c, b, ctx, 'add') : [];
    case 'mul':
      return matchMul(p, t, b, ctx);
    case 'div': {
      if (t.k === 'div')
        return matchTerm(p.a, t.a, b, ctx).flatMap((b1) => matchTerm(p.b, t.b, b1, ctx));
      if (t.k === 'num' && t.v.d !== 1n)
        return matchTerm(p.b, num(Q.rat(t.v.d)), b, ctx).flatMap((b1) =>
          matchTerm(p.a, num(Q.rat(t.v.n)), b1, ctx),
        );
      return [];
    }
    case 'pow': {
      const out: Bindings[] = [];
      if (t.k === 'pow')
        out.push(...matchTerm(p.a, t.a, b, ctx).flatMap((b1) => matchTerm(p.b, t.b, b1, ctx)));
      if (!out.length && p.b.k === 'num' && Q.isInteger(p.b.v) && p.b.v.n > 1n)
        for (const r of rootOf(t, Number(p.b.v.n))) out.push(...matchTerm(p.a, r, b, ctx));
      return out;
    }
    case 'fn':
      return t.k === 'fn' && t.f === p.f ? matchTerm(p.a, t.a, b, ctx) : [];
    case 'binom':
      return t.k === 'binom'
        ? matchTerm(p.a, t.a, b, ctx).flatMap((b1) => matchTerm(p.b, t.b, b1, ctx))
        : [];
  }
}

/** Produit : coefficient du motif divisé, puis appariement à l'ordre près (sauf matrices). */
function matchMul(p: { k: 'mul'; c: Term[] }, t: Term, b: Bindings, ctx: MatchCtx): Bindings[] {
  const coef = p.c[0]?.k === 'num' ? p.c[0].v : null;
  if (coef && !Q.isZero(coef)) {
    const rest = p.c.slice(1);
    const target = canon(mul(num(Q.div(Q.ONE, coef)!), t), ctx.sorts);
    const pat = rest.length === 1 ? rest[0]! : ({ k: 'mul', c: rest } as Term);
    return matchTerm(pat, target, b, ctx);
  }
  const tc = t.k === 'mul' ? t.c : [t];
  if (p.c.some((c) => involvesMatrix(c, ctx.sorts)) || tc.some((c) => involvesMatrix(c, ctx.sorts)))
    return matchOrdered(p.c, tc, b, ctx);
  return matchAC(p.c, tc, b, ctx, 'mul');
}

/**
 * Appariement associatif-commutatif : chaque terme de la cible va à un terme du
 * motif ; une variable de motif peut en prendre plusieurs (au moins un).
 */
function matchAC(
  ps: Term[],
  ts: Term[],
  b: Bindings,
  ctx: MatchCtx,
  op: 'add' | 'mul',
): Bindings[] {
  if (ps.length > ts.length || ts.length > 8) return [];
  // motifs non-variables d'abord (ils contraignent), variables libres ensuite
  const order = [...ps].sort((x, y) => Number(isVar(x, ctx)) - Number(isVar(y, ctx)));
  const out: Bindings[] = [];
  const go = (i: number, used: boolean[], cur: Bindings): void => {
    if (ctx.fuel.n < 0 || out.length >= 64) return;
    if (i === order.length) {
      if (used.every(Boolean)) out.push(cur);
      return;
    }
    const pi = order[i]!;
    const lastVar = i === order.length - 1 && isVar(pi, ctx);
    if (lastVar && !cur[varName(pi, ctx)!]) {
      // la dernière variable prend tout ce qui reste
      const rest = ts.filter((_, j) => !used[j]);
      if (!rest.length) return;
      const term = rest.length === 1 ? rest[0]! : ({ k: op, c: rest } as Term);
      for (const b1 of matchTerm(pi, term, cur, ctx))
        go(
          i + 1,
          used.map(() => true),
          b1,
        );
      return;
    }
    for (let j = 0; j < ts.length; j++) {
      if (used[j]) continue;
      for (const b1 of matchTerm(pi, ts[j]!, cur, ctx)) {
        const u = [...used];
        u[j] = true;
        go(i + 1, u, b1);
      }
    }
    // une variable libre non dernière peut aussi prendre un groupe de termes (paires)
    if (
      varName(pi, ctx) !== null &&
      !cur[varName(pi, ctx)!] &&
      ts.length - used.filter(Boolean).length > order.length - i
    ) {
      const free = ts.map((_, j) => j).filter((j) => !used[j]);
      for (let a = 0; a < free.length; a++)
        for (let c = a + 1; c < free.length; c++) {
          const term: Term = { k: op, c: [ts[free[a]!]!, ts[free[c]!]!] };
          for (const b1 of matchTerm(pi, term, cur, ctx)) {
            const u = [...used];
            u[free[a]!] = true;
            u[free[c]!] = true;
            go(i + 1, u, b1);
          }
        }
    }
  };
  go(
    0,
    ts.map(() => false),
    b,
  );
  return out;
}

/** Produit non commutatif : dans l'ordre, une variable prend un bloc contigu. */
function matchOrdered(ps: Term[], ts: Term[], b: Bindings, ctx: MatchCtx): Bindings[] {
  if (!ps.length) return ts.length ? [] : [b];
  const [p0, ...pr] = ps;
  const out: Bindings[] = [];
  const maxTake = isVar(p0!, ctx) ? ts.length - pr.length : 1;
  for (let k = 1; k <= maxTake; k++) {
    const head = ts.slice(0, k);
    const term = head.length === 1 ? head[0]! : ({ k: 'mul', c: head } as Term);
    for (const b1 of matchTerm(p0!, term, b, ctx))
      out.push(...matchOrdered(pr, ts.slice(k), b1, ctx));
  }
  return out;
}

/* ------------------------------ instanciation ------------------------------ */

export function instantiate(t: Term, b: Bindings, sorts: Sorts = {}): Term {
  return canon(substitute(t, b as Record<string, Term>), sorts);
}

/** Écriture simplifiée d'une valeur trouvée (k = (−x) ÷ x → −1). */
function simplified(t: Term, opts: NormalOptions): Term | null {
  const r = normalize(t, opts);
  return r.ok ? rfToTerm(rfSimplify(r.rf)) : null;
}

function unbound(t: Term, b: Bindings, ctx: MatchCtx, acc = new Set<string>()): Set<string> {
  const v = varName(t, ctx);
  if (v !== null) {
    if (!b[v]) acc.add(v);
  } else if (t.k === 'add' || t.k === 'mul') t.c.forEach((c) => unbound(c, b, ctx, acc));
  else if (t.k === 'div' || t.k === 'pow' || t.k === 'binom') {
    unbound(t.a, b, ctx, acc);
    unbound(t.b, b, ctx, acc);
  } else if (t.k === 'fn') unbound(t.a, b, ctx, acc);
  return acc;
}

/**
 * Lire les variables introduites en inversant un membre de la forme d'arrivée :
 * « ?u + ?c » = T → c = T − u ; « ?k × ?u » = T → k = T ÷ u ; « ?u ÷ ?k » = T → k = u ÷ T.
 */
function solveIntroduced(
  p: Term,
  t: Term,
  b: Bindings,
  ctx: MatchCtx,
  opts: NormalOptions,
): Bindings | null {
  const free = [...unbound(p, b, ctx)];
  if (!free.length) return b;
  if (free.length > 1) return null;
  const k = free[0]!;
  if (varName(p, ctx) !== null) return { ...b, [k]: t };
  const holds = (c: Term): boolean => unbound(c, b, ctx).size > 0;
  if (p.k === 'add') {
    const withK = p.c.filter(holds);
    if (withK.length !== 1) return null;
    const others = p.c.filter((c) => !holds(c)).map((c) => instantiate(c, b, ctx.sorts));
    const rest = others.reduce<Term>((acc, c) => sub(acc, c), t);
    return solveIntroduced(withK[0]!, canon(rest, ctx.sorts), b, ctx, opts);
  }
  if (p.k === 'mul') {
    const withK = p.c.filter(holds);
    if (withK.length !== 1) return null;
    const others = p.c.filter((c) => !holds(c)).map((c) => instantiate(c, b, ctx.sorts));
    if (!others.length) return null;
    return solveIntroduced(withK[0]!, div(t, mul(...others)), b, ctx, opts);
  }
  if (p.k === 'div') {
    if (holds(p.b) && !holds(p.a))
      return solveIntroduced(p.b, div(instantiate(p.a, b, ctx.sorts), t), b, ctx, opts);
    if (holds(p.a) && !holds(p.b))
      return solveIntroduced(p.a, mul(t, instantiate(p.b, b, ctx.sorts)), b, ctx, opts);
    return null;
  }
  return null;
}

/* ------------------------------ relations ------------------------------ */

/** Relation orientée « < » / « ≤ » / « = » (« a > b » devient « b < a »). */
function orient(
  rel: FormRelation | Rel,
  l: Term,
  r: Term,
): { rel: FormRelation; l: Term; r: Term } {
  if (rel === '>') return { rel: '<', l: r, r: l };
  if (rel === '≥') return { rel: '≤', l: r, r: l };
  return { rel, l, r };
}

/* ------------------------------ règle d'une étape ------------------------------ */

export interface RuleStepMatch {
  rule: LoadedRule;
  /** direction utilisée (clés de formes) */
  from: string;
  to: string;
  bindings: Bindings;
  /** préconditions instanciées (à décider par l'appelant) */
  conditions: Condition[];
  /** règle lue en miroir sur les deux lignes (« b = √a → b² = a ») */
  mirrored?: boolean;
}

export interface MatchOptions {
  sorts?: Sorts;
  /** essais d'appariement au plus (défaut 4000) */
  fuel?: number;
}

/** Préconditions avec les valeurs liées (« négatif : k » → « négatif : −1 »). */
function instantiateConditions(rule: LoadedRule, b: Bindings, sorts: Sorts): Condition[] | null {
  const out: Condition[] = [];
  for (const c of rule.preconditions) {
    const of: string[] = [];
    for (const name of c.of) {
      const v = b[name];
      if (!v) return null;
      const s = simplified(v, { sorts });
      of.push(termText(s ?? v));
    }
    out.push({ ...c, of });
  }
  return out;
}

/** Le passage `prev` → `next` est-il une application de `rule` ? (toutes les façons trouvées) */
export function matchRuleStep(
  rule: LoadedRule,
  prev: Statement,
  next: Statement,
  opts: MatchOptions = {},
): RuleStepMatch[] {
  const sorts = opts.sorts ?? {};
  const ctx: MatchCtx = { vars: rule.vars, sorts, fuel: { n: opts.fuel ?? 4000 } };
  const forms = ruleForms(rule);
  const out: RuleStepMatch[] = [];
  for (const d of rule.directions) {
    const fFrom = forms[d.from]!;
    const fTo = forms[d.to]!;
    const found =
      rule.appliesTo === 'expression'
        ? matchRewrite(fFrom, fTo, prev, next, ctx).map((b) => ({ b, mirrored: false }))
        : matchRelation(fFrom, fTo, prev, next, ctx);
    for (const { b, mirrored } of found) {
      const conditions = instantiateConditions(rule, b, sorts);
      if (!conditions) continue;
      out.push({
        rule,
        from: d.from,
        to: d.to,
        bindings: b,
        conditions,
        ...(mirrored ? { mirrored } : {}),
      });
      if (out.length >= rule.maxMatches) return out;
    }
  }
  return out;
}

/** Équation / inéquation : forme de départ sur `prev`, forme d'arrivée membre à membre sur `next`. */
function matchRelation(
  fFrom: RuleForm,
  fTo: RuleForm,
  prev: Statement,
  next: Statement,
  ctx: MatchCtx,
): { b: Bindings; mirrored: boolean }[] {
  if (prev.k !== 'rel' || next.k !== 'rel' || fFrom.sides.length !== 2 || fTo.sides.length !== 2)
    return [];
  const pf = orient(fFrom.relation, fFrom.sides[0]!, fFrom.sides[1]!);
  const pp = orient(prev.rel, prev.l, prev.r);
  const tf = orient(fTo.relation, fTo.sides[0]!, fTo.sides[1]!);
  const nn = orient(next.rel, next.l, next.r);
  // Une forme écrite avec « < » vaut aussi pour « ≤ » quand elle garde la largeur de
  // l'inégalité : « u < v → u + c < v + c » couvre « u ≤ v → u + c ≤ v + c ».
  const family = (r: FormRelation): FormRelation => (r === '≤' ? '<' : r);
  const keepsWidth = pf.rel === tf.rel;
  if (keepsWidth) {
    if (family(pf.rel) !== family(pp.rel) || family(tf.rel) !== family(nn.rel) || pp.rel !== nn.rel)
      return [];
  } else if (pf.rel !== pp.rel || tf.rel !== nn.rel) return [];
  const prevViews = pp.rel === '=' ? [pp, { rel: pp.rel, l: pp.r, r: pp.l }] : [pp];
  // la ligne écrite est lue telle quelle : la combiner avec la symétrie de « = »
  // ferait passer n'importe quelle simplification pour un « échange des membres » …
  // … sauf la règle lue en miroir sur les DEUX lignes (« ℓ = √(ℓ + 2) → ℓ² = ℓ + 2 » :
  // √a = b → a = b² lu de droite à gauche), quand la ligne de départ l'est aussi
  const mirror = nn.rel === '=' ? [{ rel: nn.rel, l: nn.r, r: nn.l }] : [];
  const out: { b: Bindings; mirrored: boolean }[] = [];
  const nopts = { sorts: ctx.sorts };
  // vue directe d'abord ; la vue symétrique (a = b ↔ b = a) seulement si rien n'est trouvé
  for (const pv of prevViews) {
    const nextViews = pv === pp ? [nn] : [nn, ...mirror];
    for (const b0 of matchTerm(pf.l, pv.l, {}, ctx).flatMap((b1) => matchTerm(pf.r, pv.r, b1, ctx)))
      for (const nv of nextViews) {
        // la valeur introduite se lit sur un membre, puis se vérifie sur l'autre ; un
        // membre dégénéré (« 0 » : k = 0 ÷ 0) la laisse lire sur l'autre membre (lot 4 :
        // « −5x < 0 → x > 0 » est bien « diviser par −5 »)
        const orders: [Term, Term, Term, Term][] = [
          [tf.l, nv.l, tf.r, nv.r],
          [tf.r, nv.r, tf.l, nv.l],
        ];
        let matched = false;
        for (const [p1, t1, p2, t2] of orders) {
          let b: Bindings | null = solveIntroduced(p1, t1, b0, ctx, nopts);
          if (b) b = solveIntroduced(p2, t2, b, ctx, nopts);
          if (!b) continue;
          // valeurs introduites simplifiées (une valeur incalculable : lecture écartée)
          const clean: Record<string, Term> = { ...b };
          let defined = true;
          for (const k of Object.keys(clean))
            if (!(k in b0)) {
              const sk = simplified(clean[k]!, nopts);
              if (!sk) defined = false;
              else clean[k] = sk;
            }
          if (!defined) continue;
          const l = instantiate(tf.l, clean, ctx.sorts);
          const r = instantiate(tf.r, clean, ctx.sorts);
          // application qui ne change rien à la relation de départ (ajouter 0, multiplier
          // par 1, échanger deux fois) : écartée — le pas réel est ailleurs
          const same = (v: { l: Term; r: Term }): boolean =>
            compareTerms(l, v.l, nopts) === 'equal' && compareTerms(r, v.r, nopts) === 'equal';
          const trivial = same(pp) || (nv !== nn && same(pv));
          if (trivial) continue;
          if (
            compareTerms(l, nv.l, nopts) === 'equal' &&
            compareTerms(r, nv.r, nopts) === 'equal'
          ) {
            if (!out.some((o) => sameBindings(o.b, clean)))
              out.push({ b: clean, mirrored: nv !== nn });
            matched = true;
            break;
          }
        }
        if (matched) break;
      }
    if (out.length) break;
  }
  return out;
}

function sameBindings(a: Bindings, b: Bindings): boolean {
  const ka = Object.keys(a).sort();
  const kb = Object.keys(b).sort();
  return ka.join() === kb.join() && ka.every((k) => termKey(a[k]!) === termKey(b[k]!));
}

/* --------------------------- réécriture d'expression --------------------------- */

interface Site {
  /** sous-terme ciblé */
  t: Term;
  /** reconstruit le terme entier avec ce sous-terme remplacé */
  put: (x: Term) => Term;
}

/** Positions de réécriture : tous les sous-termes, et les parties d'une somme / d'un produit. */
function sites(t: Term, put: (x: Term) => Term = (x) => x, out: Site[] = []): Site[] {
  out.push({ t, put });
  if (out.length > 400) return out;
  switch (t.k) {
    case 'add':
    case 'mul': {
      const n = t.c.length;
      t.c.forEach((c, i) =>
        sites(c, (x) => put({ k: t.k, c: t.c.map((y, j) => (j === i ? x : y)) } as Term), out),
      );
      if (n > 2 && n <= 7)
        for (let mask = 1; mask < (1 << n) - 1; mask++) {
          const bits = [...Array(n).keys()].filter((i) => mask & (1 << i));
          if (bits.length < 2) continue;
          const part: Term = { k: t.k, c: bits.map((i) => t.c[i]!) } as Term;
          const restIdx = [...Array(n).keys()].filter((i) => !(mask & (1 << i)));
          out.push({
            t: part,
            put: (x) => put({ k: t.k, c: [x, ...restIdx.map((i) => t.c[i]!)] } as Term),
          });
        }
      break;
    }
    case 'div':
    case 'pow':
    case 'binom': {
      const { a, b } = t;
      sites(a, (x) => put({ ...t, a: x }), out);
      sites(b, (x) => put({ ...t, b: x }), out);
      break;
    }
    case 'fn':
      sites(t.a, (x) => put({ ...t, a: x }), out);
      break;
    default:
      break;
  }
  return out;
}

/**
 * Réécriture d'un sous-terme de `src` par la forme `fOut` qui redonne exactement
 * `dst` (forme canonique légère). Utilisée dans les deux sens : forme de départ
 * sur la ligne de départ, ou forme d'arrivée sur la ligne écrite (factoriser
 * « 6x + 4 » en « 2(3x + 2) » se lit sur la ligne factorisée).
 */
function rewriteOneWay(
  fIn: RuleForm,
  fOut: RuleForm,
  src: Term,
  dst: Term,
  ctx: MatchCtx,
): Bindings[] {
  const out: Bindings[] = [];
  const target = termKey(canon(dst, ctx.sorts));
  for (const s of sites(canon(src, ctx.sorts))) {
    for (const b of matchTerm(fIn.sides[0]!, s.t, {}, ctx)) {
      if (unbound(fOut.sides[0]!, b, ctx).size) continue;
      const replaced = instantiate(fOut.sides[0]!, b, ctx.sorts);
      if (sameTerm(replaced, s.t, ctx.sorts)) continue;
      const rebuilt = canon(s.put(replaced), ctx.sorts);
      if (termKey(rebuilt) === target && !out.some((o) => sameBindings(o, b))) out.push(b);
    }
    if (ctx.fuel.n < 0) break;
  }
  return out;
}
function matchRewriteTerm(
  fFrom: RuleForm,
  fTo: RuleForm,
  prev: Term,
  next: Term,
  ctx: MatchCtx,
): Bindings[] {
  const fwd = rewriteOneWay(fFrom, fTo, prev, next, ctx);
  return fwd.length ? fwd : rewriteOneWay(fTo, fFrom, next, prev, ctx);
}

/** Réécriture d'expression : sur une expression, ou sur UN membre d'une relation (l'autre inchangé). */
function matchRewrite(
  fFrom: RuleForm,
  fTo: RuleForm,
  prev: Statement,
  next: Statement,
  ctx: MatchCtx,
): Bindings[] {
  if (fFrom.relation !== 'none' || fTo.relation !== 'none') return [];
  if (prev.k === 'expr' && next.k === 'expr')
    return matchRewriteTerm(fFrom, fTo, prev.e, next.e, ctx);
  if (prev.k === 'rel' && next.k === 'rel' && prev.rel === next.rel) {
    const out: Bindings[] = [];
    if (sameTerm(prev.r, next.r, ctx.sorts))
      out.push(...matchRewriteTerm(fFrom, fTo, prev.l, next.l, ctx));
    if (sameTerm(prev.l, next.l, ctx.sorts))
      out.push(...matchRewriteTerm(fFrom, fTo, prev.r, next.r, ctx));
    return out;
  }
  return [];
}

/* ------------------------------ pour la recherche ------------------------------ */

export function newMatchCtx(rule: LoadedRule, sorts: Sorts = {}, fuel = 4000): MatchCtx {
  return { vars: rule.vars, sorts, fuel: { n: fuel } };
}

/** Toutes les réécritures d'un terme par une identité (forme `fIn` → forme `fOut`). */
export function rewriteSuccessors(
  fIn: RuleForm,
  fOut: RuleForm,
  t: Term,
  ctx: MatchCtx,
): { term: Term; bindings: Bindings }[] {
  const out: { term: Term; bindings: Bindings }[] = [];
  const seen = new Set<string>();
  for (const s of sites(canon(t, ctx.sorts))) {
    for (const b of matchTerm(fIn.sides[0]!, s.t, {}, ctx)) {
      if (unbound(fOut.sides[0]!, b, ctx).size) continue;
      const replaced = instantiate(fOut.sides[0]!, b, ctx.sorts);
      if (sameTerm(replaced, s.t, ctx.sorts)) continue;
      const term = canon(s.put(replaced), ctx.sorts);
      const k = termKey(term);
      if (seen.has(k)) continue;
      seen.add(k);
      out.push({ term, bindings: b });
    }
    if (ctx.fuel.n < 0 || out.length > 32) break;
  }
  return out;
}

/** Appariements de la forme de départ d'une règle de relation sur une relation (« = » dans les deux sens). */
export function matchRelationFrom(
  fFrom: RuleForm,
  st: { rel: Rel; l: Term; r: Term },
  ctx: MatchCtx,
): Bindings[] {
  const pf = orient(fFrom.relation, fFrom.sides[0]!, fFrom.sides[1]!);
  const pp = orient(st.rel, st.l, st.r);
  const family = (r: FormRelation): FormRelation => (r === '≤' ? '<' : r);
  if (family(pf.rel) !== family(pp.rel)) return [];
  const views = pp.rel === '=' ? [pp, { rel: pp.rel, l: pp.r, r: pp.l }] : [pp];
  const out: Bindings[] = [];
  for (const v of views)
    for (const b of matchTerm(pf.l, v.l, {}, ctx).flatMap((b1) => matchTerm(pf.r, v.r, b1, ctx)))
      if (!out.some((o) => sameBindings(o, b))) out.push(b);
  return out;
}

/** Forme d'arrivée instanciée : relation (« < » gardé large si la forme garde la largeur). */
export function instantiateRelation(
  fFrom: RuleForm,
  fTo: RuleForm,
  st: { rel: Rel },
  b: Bindings,
  sorts: Sorts = {},
): { rel: Rel; l: Term; r: Term } | null {
  if (fTo.sides.length !== 2) return null;
  let rel = fTo.relation as Rel;
  // « < » d'une forme qui garde la largeur vaut « ≤ » sur une inégalité large
  const wide = st.rel === '≤' || st.rel === '≥';
  const fromRel = orient(fFrom.relation, fFrom.sides[0]!, fFrom.sides[1]!).rel;
  const toRel = orient(fTo.relation, fTo.sides[0]!, fTo.sides[1]!).rel;
  if (wide && fromRel === '<' && (toRel === '<' || toRel === '≤'))
    rel = rel === '<' ? '≤' : rel === '>' ? '≥' : rel;
  const l = instantiate(fTo.sides[0]!, b, sorts);
  const r = instantiate(fTo.sides[1]!, b, sorts);
  // « L ≥ R » a été lue « R ≤ L » : le résultat est rendu dans le sens de l'élève
  if (st.rel === '>' || st.rel === '≥') {
    const flipped: Rel =
      rel === '<' ? '>' : rel === '>' ? '<' : rel === '≤' ? '≥' : rel === '≥' ? '≤' : rel;
    return { rel: flipped, l: r, r: l };
  }
  return { rel, l, r };
}
